// Talent Tree Time Management Service
interface TalentNode {
  id: string;
  name: string;
  description: string;
  maxRank: number;
  currentRank: number;
  icon: React.ReactNode;
  row: number;
  col: number;
  requires?: string[];
  unlocked: boolean;
  timeRequired: number;
}

interface TalentTree {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: React.ReactNode;
  talents: TalentNode[];
}

interface TimeStats {
  weeklyCommitment: number;
  totalSpentTime: number;
  availableTime: number;
}

// Simplified interface for storage (without React components)
interface SavedTalentState {
  id: string;
  currentRank: number;
  unlocked: boolean;
}

interface SavedTreeState {
  treeId: string;
  talents: SavedTalentState[];
}

class TalentTreeService {
  private static instance: TalentTreeService;
  private listeners: ((stats: TimeStats) => void)[] = [];

  static getInstance(): TalentTreeService {
    if (!TalentTreeService.instance) {
      TalentTreeService.instance = new TalentTreeService();
    }
    return TalentTreeService.instance;
  }

  // Get weekly commitment from localStorage or default
  getWeeklyCommitment(): number {
    const saved = localStorage.getItem('healthTreeWeeklyCommitment');
    return saved ? parseFloat(saved) : 6;
  }

  // Set weekly commitment and save to localStorage
  setWeeklyCommitment(hours: number): void {
    localStorage.setItem('healthTreeWeeklyCommitment', hours.toString());
    this.notifyListeners();
  }

  // Save complete talent tree state to localStorage
  saveTalentTreeState(trees: TalentTree[]): void {
    try {
      const treeStates: SavedTreeState[] = trees.map(tree => ({
        treeId: tree.id,
        talents: tree.talents.map(talent => ({
          id: talent.id,
          currentRank: talent.currentRank,
          unlocked: talent.unlocked
        }))
      }));
      
      localStorage.setItem('talentTreeState', JSON.stringify(treeStates));
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving talent tree state:', error);
    }
  }

  // Load talent tree state from localStorage and apply to trees
  loadTalentTreeState(trees: TalentTree[]): TalentTree[] {
    try {
      const saved = localStorage.getItem('talentTreeState');
      if (!saved) return trees;

      const treeStates: SavedTreeState[] = JSON.parse(saved);
      
      return trees.map(tree => {
        const savedTree = treeStates.find(st => st.treeId === tree.id);
        if (!savedTree) return tree;

        return {
          ...tree,
          talents: tree.talents.map(talent => {
            const savedTalent = savedTree.talents.find(st => st.id === talent.id);
            if (!savedTalent) return talent;

            return {
              ...talent,
              currentRank: savedTalent.currentRank,
              unlocked: savedTalent.unlocked
            };
          })
        };
      });
    } catch (error) {
      console.error('Error loading talent tree state:', error);
      return trees;
    }
  }

  // Get talent ranks from localStorage (legacy support)
  getTalentRanks(): Record<string, number> {
    const saved = localStorage.getItem('talentRanks');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Error parsing saved talent ranks:', error);
      }
    }
    return {};
  }

  // Save talent trees to localStorage (legacy method - now calls new method)
  saveTalentTrees(trees: TalentTree[]): void {
    this.saveTalentTreeState(trees);
  }

  // Clear all saved talent tree data
  clearTalentTreeData(): void {
    localStorage.removeItem('talentTreeState');
    localStorage.removeItem('talentRanks');
    this.notifyListeners();
  }

  // Calculate total spent time from talent tree state
  calculateTotalSpentTime(trees: TalentTree[]): number {
    return trees.reduce((total, tree) => {
      return total + tree.talents.reduce((treeTotal, talent) => {
        return treeTotal + (talent.currentRank * talent.timeRequired);
      }, 0);
    }, 0);
  }

  // Calculate spent time from saved ranks and predefined time requirements
  calculateSpentTimeFromRanks(): number {
    const ranks = this.getTalentRanks();
    
    // Define time requirements for each talent (matching TalentTree.tsx)
    const timeRequirements: Record<string, number> = {
      'longevity-start': 0.5,
      'longevity-cellular': 1,
      'longevity-metabolic': 1.5,
      'longevity-advanced': 2,
      'healthspan-fitness': 1.5,
      'healthspan-cardio': 2,
      'healthspan-strength': 2,
      'healthspan-recovery': 1,
      'wellness-mindset': 0.5,
      'wellness-stress': 1,
      'wellness-purpose': 1,
      'wellness-flow': 1.5,
    };

    return Object.entries(ranks).reduce((total, [talentId, rank]) => {
      const timeRequired = timeRequirements[talentId] || 0;
      return total + (rank * timeRequired);
    }, 0);
  }

  // Get current time stats
  getCurrentTimeStats(trees?: TalentTree[]): TimeStats {
    const weeklyCommitment = this.getWeeklyCommitment();
    const totalSpentTime = trees 
      ? this.calculateTotalSpentTime(trees) 
      : this.calculateSpentTimeFromRanks();
    const availableTime = weeklyCommitment - totalSpentTime;

    return {
      weeklyCommitment,
      totalSpentTime,
      availableTime,
    };
  }

  // Subscribe to time stats changes
  subscribe(callback: (stats: TimeStats) => void): () => void {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of changes
  private notifyListeners(): void {
    const stats = this.getCurrentTimeStats();
    this.listeners.forEach(callback => callback(stats));
  }

  // Export talent tree data for backup/sharing
  exportTalentTreeData(): string {
    const weeklyCommitment = this.getWeeklyCommitment();
    const treeState = localStorage.getItem('talentTreeState');
    
    const exportData = {
      weeklyCommitment,
      treeState: treeState ? JSON.parse(treeState) : null,
      exportDate: new Date().toISOString()
    };

    return JSON.stringify(exportData, null, 2);
  }

  // Import talent tree data from backup
  importTalentTreeData(importData: string): boolean {
    try {
      const data = JSON.parse(importData);
      
      if (data.weeklyCommitment !== undefined) {
        this.setWeeklyCommitment(data.weeklyCommitment);
      }
      
      if (data.treeState) {
        localStorage.setItem('talentTreeState', JSON.stringify(data.treeState));
      }
      
      this.notifyListeners();
      return true;
    } catch (error) {
      console.error('Error importing talent tree data:', error);
      return false;
    }
  }
}

export const talentTreeService = TalentTreeService.getInstance();
export type { TimeStats, TalentTree, TalentNode }; 